/**
 * SellerRow — one seller as a line on the counter ledger (2026-08 revamp).
 *
 * WHY THIS IS A ROW AND NOT A CARD
 * The previous surface rendered every seller as a floating white card with an
 * initials disc, a bold heading, a grey meta paragraph and a bare chevron —
 * the exact "icon chip + heading + grey paragraph" template the direction
 * bans, and a shape that hides the only three facts a buyer standing in a
 * bazaar needs. This is the same three facts on the design direction's LABEL
 * GRID, ruled like a товарный чек:
 *
 *     [nn]  NAME                                        FIGURE
 *           market · container  (the .g-data measure)     unit
 *           ★ rating · reviews   [warranty] [langs]
 *
 * Every row is ruled by that identical triplet, so a wall of them reads as one
 * instrument (RAISE: label grid). The rules between rows are DRAWN by the
 * parent's `.g-ruled` (RAISE: visible armature) — there is no card, no shadow
 * and no nesting.
 *
 * HONEST ZEROES (PRODUCT.md "Absences future work must not fabricate")
 * The live catalogue really does return `rating: null`, `reviews_count: 0` and
 * `positions_count: 0` for its sellers. The old row ran those through
 * `parseDecimal(null) → 0` and printed a gold star reading "0.0 · 0 отзывов",
 * which invents a *bad rating* out of *no rating*. Here:
 *   - the rating line renders ONLY when a real rating and ≥1 review exist;
 *   - a zero position count prints the ledger's nil mark — an em dash in the
 *     figure column under the caption "нет данных" — instead of a "0",
 *     and the row itself explains what to do about it (call and ask).
 * A zero is a fact about the catalogue, not about the seller.
 *
 * And it is not even a reliable fact about the catalogue: the API currently
 * returns `positions_count: 0` for both seeded sellers while 421 of their
 * products are live on the marketplace. So the copy says "нет данных", not
 * "не опубликовано" — the first is true whatever the number turns out to mean,
 * the second asserts a publication state the marketplace visibly contradicts.
 * A user who sees both surfaces in one session must not be told two things.
 *
 * The leading `01/02` gutter is not decoration: it is the key that binds this
 * row to its pin on the locator plan (`SellerLocatorPlan`), which prints the
 * same number. Hover/focus of the row lights that pin and vice versa.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Star } from "lucide-react";

import { iconProps } from "@/lib/icons";
import { parseDecimal } from "@/lib/format";
import type { SellerSummary } from "@/lib/garden-api";
import type { SupportedLanguage } from "@/lib/i18n";
import { languageCode } from "@/lib/language-code";

export interface SellerRowProps {
  seller: SellerSummary;
  /** 1-based position in the ledger; also the seller's plan-pin number. */
  index: number;
  /** Opens the seller detail sheet. */
  onOpen: (seller: SellerSummary) => void;
  /** Row hover/focus — lights the matching plan pin (null on leave/blur). */
  onHover?: (sellerId: string | null) => void;
  /** The sheet is open on this seller. */
  selected?: boolean;
  /** The plan pin for this seller is hovered. */
  highlighted?: boolean;
  /**
   * Change-persistence (RAISE): the row the user last opened keeps the
   * `--g-mark` wash after the sheet closes, so returning to a scrolled ledger
   * still shows where they were. It clears when another row is opened.
   */
  marked?: boolean;
  /** UI language — picks the market-name fallback (SCRUM-144). */
  lang: SupportedLanguage;
}

/** Up-to-two-character initials for an avatar (RU/KY business names). */
export function sellerInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`.toUpperCase();
}

/**
 * The market name in the active language. SCRUM-144: `district` has no
 * per-language variant (schema/API change, out of scope) — only the market
 * name can be localized from the fields already on the wire.
 */
export function sellerMarketName(
  seller: SellerSummary,
  lang: SupportedLanguage,
): string {
  return lang === "ky"
    ? (seller.market_name_ky ?? seller.market_name_ru)
    : seller.market_name_ru;
}

/** `Уста · Б37-39` — the stall code line, set in the instrument face. */
export function sellerStallCode(
  seller: SellerSummary,
  lang: SupportedLanguage,
): string {
  const market = sellerMarketName(seller, lang);
  const pavilion = seller.pavilion_number.trim();
  return pavilion ? `${market} · ${pavilion}` : market;
}

export function SellerRow({
  seller,
  index,
  onOpen,
  onHover,
  selected = false,
  highlighted = false,
  marked = false,
  lang,
}: SellerRowProps): ReactElement {
  const { t } = useTranslation("shops");

  // A rating only exists when the catalogue actually carries one AND someone
  // has reviewed. Anything else prints no star at all (see header comment).
  const ratingValue =
    seller.rating !== null && seller.reviews_count > 0
      ? parseDecimal(seller.rating)
      : null;
  const hasStock = seller.positions_count > 0;
  const stallCode = sellerStallCode(seller, lang);

  // Resting / hovered / highlighted-from-the-plan / selected / marked. Only
  // one wash applies at a time; selection outranks the return marker.
  const stateClass = selected
    ? "bg-[var(--g-primary-soft)]"
    : highlighted
      ? "bg-[var(--g-sunken)]"
      : marked
        ? "g-mark hover:brightness-[0.98]"
        : "hover:bg-[var(--g-sunken)]";

  return (
    <button
      type="button"
      data-testid={`seller-card-${seller.id}`}
      data-selected={selected ? "true" : "false"}
      onClick={() => onOpen(seller)}
      onMouseEnter={() => onHover?.(seller.id)}
      onMouseLeave={() => onHover?.(null)}
      onFocus={() => onHover?.(seller.id)}
      onBlur={() => onHover?.(null)}
      className={[
        "grid w-full grid-cols-[1.75rem_minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1",
        "px-3 py-3.5 text-left sm:px-4",
        "transition-colors duration-150 ease-[var(--g-ease)]",
        "focus-visible:outline-none focus-visible:shadow-[inset_0_0_0_2px_var(--g-primary)]",
        "active:bg-[var(--g-primary-soft)]",
        stateClass,
      ].join(" ")}
    >
      {/* Plan key — the same figure this seller's pin carries. */}
      <span
        aria-hidden
        className={[
          "g-data mt-[3px] text-[11px] leading-none",
          selected ? "text-[var(--g-primary)]" : "text-[var(--g-ink-2)]",
        ].join(" ")}
      >
        {String(index).padStart(2, "0")}
      </span>

      <span className="min-w-0">
        <span
          className="block truncate text-[15px] font-bold leading-tight tracking-[-0.01em] text-foreground"
          title={seller.business_name}
        >
          {seller.business_name}
        </span>

        {/* The measure line: where this seller physically stands. */}
        <span className="mt-1 block text-[12px] leading-snug">
          <span className="g-data text-[var(--g-ink-2)]">{stallCode}</span>
          {seller.district !== null ? (
            <span className="text-[var(--g-ink-2)]"> · {seller.district}</span>
          ) : null}
        </span>

        {ratingValue !== null ? (
          <span className="mt-1 flex flex-wrap items-center gap-1.5 text-[12px]">
            <Star
              {...iconProps("xs")}
              className="fill-[var(--g-warn)] text-[var(--g-warn)]"
            />
            <span className="g-data font-bold text-foreground">
              {ratingValue.toFixed(1)}
            </span>
            <span className="text-[var(--g-ink-2)]">
              · {t("reviews", { count: seller.reviews_count })}
            </span>
          </span>
        ) : null}

        {seller.warranty || seller.languages.length > 0 ? (
          <span className="mt-2 flex flex-wrap items-center gap-1.5">
            {seller.warranty ? (
              <span className="inline-flex items-center gap-1 rounded-[var(--g-radius-sm)] border border-[var(--g-rule)] px-1.5 py-0.5 text-[11px] font-semibold text-[var(--g-ink-2)]">
                <ShieldCheck
                  {...iconProps("xs")}
                  className="text-[var(--g-success)]"
                />
                {t("warranty")}
              </span>
            ) : null}
            {seller.languages.length > 0 ? (
              <span className="g-data text-[11px] uppercase text-[var(--g-ink-2)]">
                {seller.languages.map(languageCode).join(" · ")}
              </span>
            ) : null}
          </span>
        ) : null}
      </span>

      {/* The figure column. Every row prints one, so they rule vertically. */}
      <span className="flex max-w-[7.5rem] flex-col items-end text-right">
        <span
          aria-hidden
          className={[
            "g-data text-[19px] font-medium leading-none",
            hasStock ? "text-[var(--g-figure)]" : "text-[var(--g-ink-3)]",
          ].join(" ")}
        >
          {hasStock ? seller.positions_count : "—"}
        </span>
        <span className="mt-1 text-[10.5px] font-semibold uppercase leading-tight tracking-[0.06em] text-[var(--g-ink-2)]">
          {hasStock
            ? t("stockUnit", { count: seller.positions_count })
            : t("stockNone")}
        </span>
      </span>
    </button>
  );
}
